import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../../assets/waseeny-logo.png';
import './OrderSuccess.css';
import { useTranslate } from "../../Hook/useTranslate";

import paymentAPI from '../../services/paymentAPI';
import { getCurrencyFromPhone } from '../../utils/phone';
import { formatMoney } from '../../utils/formatMoney';

const OrderSuccess = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const t = useTranslate();
  const [countdown, setCountdown] = useState(10);
  const [verifying, setVerifying] = useState(false);

  const [order, setOrder] = useState(() => {
    const locOrder = location.state?.order;
    const symbol = getCurrencyFromPhone(locOrder?.restaurant?.contactNumber);
    return locOrder || {
      id: `ORD${Date.now()}`,
      total: `${symbol}0`,
      status: 'placed',
      items: []
    };
  });

  // Verify payment if coming from Stripe redirect
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const sessionId = searchParams.get('session_id');

    if (sessionId && !location.state?.order) {
      const verify = async () => {
        try {
          setVerifying(true);
          const res = await paymentAPI.verifySession(sessionId);
          if (res.success && res.order) {
            const symbol = getCurrencyFromPhone(res.order.restaurant?.contactNumber);
            setOrder({
              id: res.order._id,
              total: formatMoney(res.order.totalAmount, { symbol }),
              status: res.order.status,
              items: res.order.items,
              restaurant: res.order.restaurant
            });
          }
        } catch (err) {
          console.error("Verification failed:", err);
        } finally {
          setVerifying(false);
        }
      };
      verify();
    }
  }, [location.search, location.state]);

  // Auto-redirect to tracking after 10 seconds
  useEffect(() => {
    if (verifying) return;
    const timer = setInterval(() => {
      setCountdown(prev => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate('/order-tracking', { state: { order } });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(timer);
  }, [navigate, order, verifying]);

  const handleContinueTracking = () => {
    navigate('/order-tracking', { state: { order } });
  };

  const handleBackToHome = () => {
    navigate('/home');
  };

  return (
    <div className="order-success-container">
      <header className="order-success-header">
        <img src={logo} alt="Waseeny" className="order-success-logo" />
      </header>

      <main className="order-success-main">
        <div className="success-icon-wrapper">
          <div className="success-icon">
            <svg viewBox="0 0 100 100" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="50" cy="50" r="45" stroke="#4CAF50" strokeWidth="3" />
              <path
                d="M30 50L45 65L70 35"
                stroke="#4CAF50"
                strokeWidth="4"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
        </div>

        <div className="success-content">
          <h1 className="success-title">{verifying ? "Verifying Payment..." : `${t("orderConfirmed")}!`}</h1>
          <p className="success-subtitle">
            {verifying ? "Checking status with Stripe..." : t("orderPlacedSuccess")}
          </p>

          <div className="order-details-card">
            <div className="detail-row">
              <span className="detail-label">{t("orderIdLabel")}:</span>
              <span className="detail-value">{order.id?.toString().slice(-8).toUpperCase()}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">{t("totalAmountLabel")}:</span>
              <span className="detail-value amount">{order.total}</span>
            </div>
            <div className="detail-row">
              <span className="detail-label">{t("statusLabel")}:</span>
              <span className="detail-value status">{verifying ? "Wait..." : t("preparing")}</span>
            </div>
          </div>

          <div className="success-actions">
            <button className="btn btn-primary" onClick={handleContinueTracking} disabled={verifying}>
              {t("trackOrder")}
            </button>
            <button className="btn btn-secondary" onClick={handleBackToHome}>
              {t("backToHome")}
            </button>
          </div>

          {!verifying && (
            <p className="countdown-message">
              {t("redirectingToTracking")} <strong>{countdown}</strong> {t("seconds")}...
            </p>
          )}
        </div>
      </main>
    </div>
  );
};

export default OrderSuccess;